import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_BASE } from '../../utils/hosts';
import { DownloadIcon, PlusIcon, Trash2Icon, UploadIcon, WandSparklesIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Panel } from '../ui/Panel';
import { useAdmin } from '../AdminContext';
import type { RedirectsState } from './useRedirects';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnGhost, btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { mergeRedirects, normalizePath, normalizeTarget, parseRedirectsCsv, redirectsCsv, wooSuggestions } from '../../core/rules/redirects';
import { fromIso, longDate, today } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { downloadFile } from '../../utils/download';

/**
 * Instellingen › Vindbaarheid & pers: oude adressen doorverwijzen (301) en de stand van de sitemap. De
 * doorverwijzingen komen van de pagina (useRedirects in Settings) en gaan mee met de Opslaan-balk.
 */
export function SeoSettings({
  redirects: r
}: {
  redirects: RedirectsState;
}) {
  const {
    catalog
  } = useAdmin();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const suggestions = r.data ? wooSuggestions(catalog.products, r.items, today()) : [];
  const add = (e: FormEvent) => {
    e.preventDefault();
    const f = normalizePath(from);
    const t = normalizeTarget(to);
    if (!f) return setFormError('Het oude adres moet een pad op beautybewust.nl zijn, bijv. /product/serum.');
    if (!t) return setFormError('Het nieuwe adres moet een pad op de site zijn, of een volledige https-link.');
    setFormError(null);
    r.edit(list => mergeRedirects(list, [{
      from: f,
      to: t,
      createdAt: today()
    }]));
    setFrom('');
    setTo('');
  };
  const importCsv = async (file?: File) => {
    if (!file) return;
    const {
      items,
      rejected
    } = parseRedirectsCsv(await file.text(), today());
    if (items.length) r.edit(list => mergeRedirects(list, items));
    toast(items.length ? `${plural(items.length, 'regel', 'regels')} ingelezen` : 'Niets ingelezen', {
      description: rejected.length ? `${plural(rejected.length, 'regel', 'regels')} overgeslagen (regel ${rejected.slice(0, 5).map(x => x.line).join(', ')}). Twee kolommen: oud;nieuw.` : 'Controleer de lijst en sla op.'
    });
    if (fileRef.current) fileRef.current.value = '';
  };
  const exportCsv = () => downloadFile(redirectsCsv(r.items), 'doorverwijzingen.csv', 'text/csv;charset=utf-8');
  if (r.status === 'error' && !r.data) {
    return <Panel title="Vindbaarheid" data-id="element-4097" data-mp="gmlzwvf1j1ji">
        <LoadFailed variant="inline" title="De doorverwijzingen konden niet laden" onRetry={r.reload} data-id="element-4098" data-mp="glkcrskr2olf" />
      </Panel>;
  }
  if (!r.data) return <LoadingLine label="Doorverwijzingen laden…" className="py-8 text-center" data-id="element-4099" data-mp="31dcs5jk8flx3" />;
  return <>
      <Panel title="Sitemap" description="De lijst met pagina's die Google leest. De server maakt hem elke nacht en na elke wijziging opnieuw." data-id="element-4100" data-mp="2swc78mhqqmdj">
        <p className="text-[14px] text-ink" data-id="element-4101" data-mp="2otz0bur9cif6">
          <span className="font-display text-3xl font-light tabular-nums" data-id="element-4102" data-mp="32ui42poox2bj">{r.data.sitemap.pages}</span> pagina's
        </p>
        <p className="mt-1 text-[13px] text-muted" data-id="element-4103" data-mp="2xux7un0wn82i">
          Laatst bijgewerkt {longDate(fromIso(r.data.sitemap.updatedAt.slice(0, 10)))} om {r.data.sitemap.updatedAt.slice(11, 16)} ·{' '}
          <a href="https://beautybewust.nl/sitemap.xml" target="_blank" rel="noreferrer" className="text-ink underline underline-offset-2" data-id="element-4104" data-mp="25718fajyb221">
            sitemap.xml
          </a>
        </p>
        {/* SEO staat op twee plekken; zo vind je de andere helft. */}
        <p className="mt-4 border-t border-line pt-3 text-[13px] text-muted" data-id="element-4105" data-mp="2f3mdtwzvuf6e">
          Titel en omschrijving in Google per pagina pas je aan in{' '}
          <Link to={`${ADMIN_BASE}/teksten`} className="text-ink underline underline-offset-2" data-id="element-4106" data-mp="1mh1qd7mj2n84">
            Paginateksten
          </Link>
          ; per product, behandeling of artikel in de editor daarvan. Welke pagina's weinig geklikt worden, zie je in{' '}
          <Link to={`${ADMIN_BASE}/inzicht?tab=bereik&bereik=google`} className="text-ink underline underline-offset-2" data-id="element-4107" data-mp="ullm54bsokiu">
            Inzicht › Bereik
          </Link>
          .
        </p>
      </Panel>

      <Panel flush title="Doorverwijzingen" description="Een oude link (uit Google, een bladwijzer of Instagram) gaat zo naar de juiste nieuwe pagina, en houdt zijn plek in Google." actions={<span className="text-[13px] tabular-nums text-muted" data-id="element-4109" data-mp="1idtutwymbpbp">{r.items.length}</span>} data-id="element-4108" data-mp="13i4by7oqbsb7">
        {suggestions.length > 0 && <div className="flex flex-wrap items-center gap-3 border-b border-line bg-sand/60 px-4 py-3 sm:px-5" data-id="element-4110" data-mp="1mdui873p3auw">
            <p className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-4111" data-mp="3k6i0se2ncqv6">
              {plural(suggestions.length, 'product', 'producten')} uit de oude WooCommerce-winkel {suggestions.length === 1 ? 'heeft' : 'hebben'} nog geen doorverwijzing.
            </p>
            <button type="button" onClick={() => r.edit(list => mergeRedirects(list, suggestions))} className={btnSecondary} data-id="element-4112" data-mp="3bwj9tiupecv5">
              <WandSparklesIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4113" data-mp="36y7zz84es2cx" />
              Voeg ze toe
            </button>
          </div>}

        <form onSubmit={add} className="grid gap-2 border-b border-line px-4 py-4 sm:grid-cols-[1fr_1fr_auto] sm:px-5" aria-describedby={formError ? 'redirect-error' : undefined} data-id="element-4114" data-mp="3g7ohizn9euk">
          <label data-id="element-4115" data-mp="3uky1878xnswh">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4116" data-mp="3qkx9xm0qjn80">Oud adres</span>
            <input value={from} onChange={e => setFrom(e.target.value)} placeholder="/product/oud-serum" className={inputCls} autoCapitalize="off" spellCheck={false} data-id="element-4117" data-mp="3qn0agzzikyqz" />
          </label>
          <label data-id="element-4118" data-mp="2f3xjkvxy7igm">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4119" data-mp="2a6xdwm50ghq1">Nieuw adres</span>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="/webshop/serum" className={inputCls} autoCapitalize="off" spellCheck={false} data-id="element-4120" data-mp="2a90f82bm5xa2" />
          </label>
          <button type="submit" className={`${btnSecondary} self-end`} data-id="element-4121" data-mp="2gmnty2rud1y7">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4122" data-mp="22o6c2lomajo7" />
            Toevoegen
          </button>
          {formError && <p id="redirect-error" role="alert" className="text-[12px] text-clay sm:col-span-3" data-id="element-4123" data-mp="26r2ti92f89qk">
              {formError}
            </p>}
        </form>

        {r.items.length === 0 ? <p className="px-5 py-8 text-center text-[13px] text-muted" data-id="element-4124" data-mp="1xq4r1v7msaa3">Nog geen doorverwijzingen.</p> : <div className="overflow-x-auto" data-id="element-4125" data-mp="1nyi97g0sfcyd">
            <table className="w-full text-left text-[13px]" data-id="element-4126" data-mp="1nza2ulatr0m0">
              <thead className="text-[12px] text-muted" data-id="element-4127" data-mp="19yzcrkhc9q1w">
                <tr className="border-b border-line" data-id="element-4128" data-mp="19zit4wrcc6dt">
                  <th scope="col" className="px-4 py-2 font-normal sm:px-5" data-id="element-4129" data-mp="1ey3jm4gsjbp2">Oud</th>
                  <th scope="col" className="px-2 py-2 font-normal" data-id="element-4130" data-mp="vbs7nvejbmax">Nieuw</th>
                  <th scope="col" className="sr-only" data-id="element-4131" data-mp="vdv7f75hotsu">Verwijderen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-4132" data-mp="14cqfypw0afzb">
                {r.items.map((item, i) => <tr key={item.from} className="align-top" data-id="element-4133" data-mp="10b4y0q4m7bi9" data-mpi={i}>
                    <td className="max-w-[16rem] break-all px-4 py-3 font-mono text-ink sm:px-5" data-id="element-4134" data-mp="109tqcf81tjlv">{item.from}</td>
                    <td className="max-w-[18rem] px-2 py-3" data-id="element-4135" data-mp="1e8tezmvk9yi1">
                      <span className="break-all font-mono text-ink" data-id="element-4136" data-mp="19aruqymeft4z">{item.to}</span>
                      {r.issues[i].map(issue => <span key={issue.text} className={`mt-1 block text-[12px] ${issue.level === 'error' ? 'text-clay' : 'text-muted'}`} data-id="element-4137" data-mp="1o2shimzjrkai">
                          {issue.level === 'error' ? 'Fout: ' : 'Let op: '}
                          {issue.text}
                        </span>)}
                    </td>
                    <td className="w-12 py-2 pr-3 text-right" data-id="element-4138" data-mp="2zf9vkbm592x1">
                      <button type="button" onClick={() => r.edit(list => list.filter(x => x.from !== item.from))} className={btnIcon} aria-label={`Doorverwijzing van ${item.from} verwijderen`} data-id="element-4139" data-mp="2zh327qwrbn51">
                        <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4140" data-mp="38dlsswhimyyg" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}

        <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 sm:px-5" data-id="element-4141" data-mp="2kqvsbzktfcfy">
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="sr-only" id="redirect-csv" onChange={e => importCsv(e.target.files?.[0])} data-id="element-4142" data-mp="2pnvwu620o74y" />
          <label htmlFor="redirect-csv" className={`${btnGhost} cursor-pointer`} data-id="element-4143" data-mp="1x0janwzrq1c6">
            <UploadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4144" data-mp="1wyf7jh9p16cy" />
            CSV importeren
          </label>
          <button type="button" onClick={exportCsv} disabled={!r.items.length} className={btnGhost} data-id="element-4145" data-mp="1ugd6bo78s8v3">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4146" data-mp="1pgh49ui3uri6" />
            Exporteren
          </button>
          {r.dirty && <p role="status" className={`ml-auto text-[12px] ${r.errors > 0 ? 'text-clay' : 'text-muted'}`} data-id="element-4147" data-mp="1kjqnt62e1tw8">
              {r.errors > 0 ? `Nog ${plural(r.errors, 'fout', 'fouten')}: los ze op voor je opslaat` : 'Gaat mee met Opslaan'}
            </p>}
        </div>
      </Panel>
    </>;
}